import { computed, ComputedRef } from 'vue';
import { I18n } from 'vue-i18n';
import loadLanguage from './language-loader';
import { persistSelectedLanguage } from './store';
import { LanguageDefinition } from './types';

export type UseLocalisation = () => {
  supportedLanguages: LanguageDefinition[];
  language: ComputedRef<string>;
  isLanguageSupported: (language: string) => boolean;
  changeLanguage: (language: string) => Promise<void>;
  addAdditionalTranslation: (content: object) => void;
  /**
   *
   * @param projectName
   * @param {boolean} isoLanguage [isoLanguage=true]
   * @param {boolean} subProject [subProject=false] projects that requires main project to be loaded before merging the translation
   */
  includeAdditionalProject: (projectName: string, isoLanguage?: boolean, subProject?: boolean) => Promise<void>;
  getLanguageNameWithIso: (iso: string) => string;
};

export type AdditionalProject = {
  name: string;
  isoLanguage?: boolean;
  subProject?: boolean;
};

const createUseLocalisation = (
  appName: string,
  i18n: I18n<any, any, any, string, false>,
  supportedLanguages: LanguageDefinition[],
  defaultLanguageName: string,
  additionalProjects: AdditionalProject[] = [],
): UseLocalisation => {
  const projects: AdditionalProject[] = [...additionalProjects];
  const isLanguageSupported = (language: string): boolean => {
    return supportedLanguages.some((l) => l.iso === language) || language === defaultLanguageName;
  };

  const languageToDownload = (language: string, isoLanguage = false): string =>
    // to pull project translations from lokalise where language iso codes may vary
    // for example, for app ui translations it will return 'sv-SE' and for keywords – 'sv'
    isoLanguage ? language.split('-')[0] : language;

  const language = computed(() => i18n.global.locale.value); // wrap it as read-only

  const loadAdditionalProject = (project: AdditionalProject): Promise<void> =>
    loadLanguage(project.name, languageToDownload(language.value, project.isoLanguage)).then((msgs) => {
      if (msgs) {
        i18n.global.mergeLocaleMessage(language.value, msgs);
      }
    });
  const includeAdditionalProject = (projectName: string, isoLanguage = true, subProject = false): Promise<void> => {
    const project: AdditionalProject = { name: projectName, isoLanguage, subProject };
    projects.push(project);
    return loadAdditionalProject(project);
  };

  /**
   * BE CAREFUL! You may overwrite current translations.
   * allows adding additional translations to the current language.
   */
  const addAdditionalTranslation = (content: object) => {
    i18n.global.mergeLocaleMessage(language.value, content);
  };

  const changeLanguage = async (newLanguage: string): Promise<void> => {
    if (!isLanguageSupported(newLanguage)) {
      throw new Error(
        `Language "${newLanguage}" not supported. Supported languages: ${supportedLanguages.map((l) => l.iso)
          .join(', ')}`,
      );
    }

    if (language.value === newLanguage) {
      return;
    }

    const subProjects = projects.filter((p) => p.subProject);
    const mainProjects = projects.filter((p) => !p.subProject);

    const mainAppLoading = loadLanguage(appName, newLanguage).then((msgs) => {
      i18n.global.setLocaleMessage(newLanguage, msgs);

      i18n.global.locale.value = newLanguage;

      document.querySelector('html')!.setAttribute('lang', newLanguage);
      persistSelectedLanguage(newLanguage);
    });

    const additionalAppsLoading = mainProjects.map((project) => {
      return loadLanguage(project.name, languageToDownload(newLanguage, project.isoLanguage)).then((msgs) => {
        if (msgs) {
          i18n.global.mergeLocaleMessage(newLanguage, msgs);
        }
      });
    });

    const additionalSubApps = subProjects.map(async (project) => {
      const msgs = await loadLanguage(project.name, languageToDownload(newLanguage, project.isoLanguage));
      // sub project translations are downloaded in parallel but merge into main when the main is already loaded.
      await mainAppLoading.then(() => {
        if (msgs) {
          i18n.global.mergeLocaleMessage(newLanguage, msgs);
        }
      });
    });

    await Promise.all([mainAppLoading, ...additionalAppsLoading, ...additionalSubApps]);
  };

  const getLanguageNameWithIso = (iso: string): string => supportedLanguages.find((l) => l.iso === iso)?.name ?? iso;
  return () => ({
    supportedLanguages,
    language,
    isLanguageSupported,
    changeLanguage,
    includeAdditionalProject,
    getLanguageNameWithIso,
    addAdditionalTranslation,
  });
};

export default createUseLocalisation;
